import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { ArrowLeft } from "lucide-react";
import {
	EmbeddedCheckoutProvider,
	EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { getTierForSeats, tierPricing } from "../tierPricing";

const stripeKey = process.env.REACT_APP_STRIPE_KEY;
const stripeTestKey = process.env.REACT_APP_STRIPE_TEST_KEY;

const SubscriptionSetup = ({ onSuccess }) => {
	const [clientSecret, setClientSecret] = useState("");
	const [seats, setSeats] = useState(3);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const { sendRequest } = useHttpClient();

	let stripePromise;
	if (process.env.NODE_ENV === "development") {
		stripePromise = loadStripe(stripeTestKey);
	} else {
		stripePromise = loadStripe(stripeKey);
	}

	const createCheckoutSession = async () => {
		setLoading(true);
		setError(null);

		try {
			const response = await sendRequest(
				`/api/subscriptions/create`,
				"POST",
				JSON.stringify({ seats }),
				{ "Content-Type": "application/json" }
			);
			if (response?.data?.clientSecret) {
				setClientSecret(response.data.clientSecret);
			} else {
				throw new Error("Failed to create checkout session");
			}
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const options = {
		clientSecret,
		onComplete: () => {
			// Subscription successful
			if (onSuccess) {
				onSuccess();
			}
		},
	};

	let tier = null;
	try {
		tier = getTierForSeats(seats);
	} catch (err) {
		tier = { label: "Unsupported Tier", amount: 0 };
	}

	const pricePerSeat = tier ? tier.amount / seats / 100 : "0.00";
	const totalFormatted = tier ? (tier.amount / 100).toFixed(2) : "0.00";

	return (
		<div className="subscription-setup">
			<div className="subscription-header-with-back">
				{clientSecret && (
					<button
						className="subscription-back-button"
						onClick={() => {
							setClientSecret("");
							setError(null);
						}}
					>
						<ArrowLeft className="w-5 h-5" />
					</button>
				)}
				<div className="subscription-header">
					<h2>Set Up Your Subscription</h2>
					<p>Choose the number of seats for your organization</p>
				</div>
			</div>
			{!clientSecret ? (
				<div className="seat-selector">
					<div className="tier-boxes">
						{tierPricing.map((option) => (
							<button
								key={option.maxSeats}
								className={`tier-box ${
									seats === option.maxSeats ? "active" : ""
								}`}
								onClick={() => setSeats(option.maxSeats)}
							>
								<div className="tier-box-label">{option.label}</div>
								<div className="tier-box-price">
									${(option.amount / option.maxSeats / 100).toFixed(2)} / seat
								</div>
							</button>
						))}
					</div>

					<div className="pricing-summary">
						<div className="price-breakdown">
							<div className="tier-badge">{tier ? tier.label : "N/A"}</div>
							<div>
								<strong>Price per Seat: </strong> ${pricePerSeat.toFixed(2)}
								/month
							</div>
							<div className="total">
								<strong>Total: ${totalFormatted}/month</strong>
							</div>
						</div>
					</div>

					{error && <div className="error-message">{error}</div>}

					<button
						onClick={createCheckoutSession}
						disabled={loading || seats < 3}
						className="setup-subscription-btn"
					>
						{loading ? "Setting up..." : "Set Up Subscription"}
					</button>
				</div>
			) : (
				<div className="embedded-checkout">
					<EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
						<EmbeddedCheckout />
					</EmbeddedCheckoutProvider>
				</div>
			)}
		</div>
	);
};

export default SubscriptionSetup;
